import { useCallback, useRef, useState } from 'react';
import { message } from 'antd';
import { posService } from '@/services/pos.service';
import { mapDetailToPosVariant } from '../utils/mapVariantDetail';
import { findComboShortages } from '../utils/addComboToCart';
import type {
  FulfillmentMode,
  PosComboSearchItem,
  PosVariant,
  PosVariantDetailItem,
} from '../types';

interface UsePosCartAdderOptions {
  fulfillmentMode: FulfillmentMode;
  onVariantFound: (variant: PosVariant) => void;
  onComboFound?: (combo: PosComboSearchItem, details: PosVariantDetailItem[]) => void;
  /** TCR-1143: khác onVariantFound — caller cần biết giá flash + item id để gắn lên dòng hàng. */
  onFlashSaleFound?: (variant: PosVariant, flashSaleItemId: string, flashPrice: number) => void;
  /** Gọi sau khi thêm thành công — QRSearchBar dùng để clear ô + focus lại. */
  onAdded?: () => void;
}

/**
 * Đường DUY NHẤT để thêm hàng vào giỏ POS từ một gợi ý (dropdown tìm kiếm hoặc
 * lưới gợi ý sản phẩm). Gộp ở đây vì mọi cổng kiểm tra — combo chứa serial ở
 * INSTANT, tồn THẬT qua `findComboShortages`, lỗi lookup từng mã — phải giống
 * nhau ở mọi lối vào; nhân bản logic là cách chắc chắn nhất để hai lối vào lệch
 * nhau sau vài lần sửa.
 */
export function usePosCartAdder({
  fulfillmentMode,
  onVariantFound,
  onComboFound,
  onFlashSaleFound,
  onAdded,
}: UsePosCartAdderOptions) {
  const [isAdding, setIsAdding] = useState(false);
  const isAddingRef = useRef(false);

  const selectCombo = useCallback(
    async (combo: PosComboSearchItem) => {
      if (isAddingRef.current) return;
      // Màn không truyền `onComboFound` = không hỗ trợ combo — không có
      // đường nào trong component còn gọi tới đây khi thiếu prop, guard này
      // chỉ để phòng vệ (và giúp TS thu hẹp kiểu cho lần gọi bên dưới).
      if (!onComboFound) return;

      // Chặn sớm, không tốn round-trip: `lookupVariants` yêu cầu định dạng
      // `variant_code_serial` cho hàng serial ở INSTANT, tức phải quét từng
      // serial cụ thể — combo không cung cấp được thông tin đó.
      if (combo.has_serial_component && fulfillmentMode === 'INSTANT') {
        void message.warning('Combo chứa hàng serial, vui lòng thêm từng SP rồi gộp combo');
        return;
      }

      isAddingRef.current = true;
      setIsAdding(true);
      try {
        const codes = combo.component_items.map((component) => component.variant_code);
        const { items, item_errors } = await posService.lookupVariants(codes, fulfillmentMode);
        if (item_errors.length > 0) {
          void message.error(item_errors[0].error);
          return;
        }
        if (items.length < combo.component_items.length) {
          void message.error('Combo có sản phẩm không còn bán được');
          return;
        }

        // Cổng tồn THẬT — số ở card là số cache 30s, chỉ để vẽ nhãn.
        const shortages = findComboShortages(combo, items, fulfillmentMode);
        if (shortages.length > 0) {
          void message.error(
            `Không đủ tồn cho combo: ${shortages
              .map((s) => `${s.product_name} cần ${s.needed}, còn ${s.available}`)
              .join('; ')}`,
          );
          return;
        }

        onComboFound(combo, items);
        onAdded?.();
      } catch {
        void message.error('Lỗi khi lấy thông tin combo');
      } finally {
        isAddingRef.current = false;
        setIsAdding(false);
      }
    },
    [fulfillmentMode, onComboFound, onAdded],
  );

  const addByQrCode = useCallback(
    async (qrValue: string) => {
      if (isAddingRef.current) return;
      isAddingRef.current = true;
      setIsAdding(true);
      try {
        const lookup = await posService.lookupVariants([qrValue], fulfillmentMode);
        let detail = lookup.items[0];

        if (!detail) {
          // TCR-1371 — KHÔNG báo lỗi ngay ở đây.
          //
          // Ba loại người dùng đi chung một ô nhập: súng quét (bắn cả chuỗi
          // kèm Enter), thu ngân gõ mã, và thu ngân gõ vài chữ của TÊN sản
          // phẩm rồi Enter luôn — người thứ ba nhấn Enter trước khi dropdown
          // gợi ý kịp về (debounce 500ms ở `useQrVariantSearch`), nên chuỗi
          // đang gõ bị đem đi tra như một mã. `lookupVariants` chỉ hiểu
          // variant_code, nên cả chuỗi tìm kiếm LẪN mã combo đều rơi vào
          // nhánh này. Tra thêm một lần theo đường tìm kiếm rồi mới kết luận.
          //
          // Trước bản vá, khối này nằm sau một `if (item_errors.length > 0)`
          // trả về sớm — mà mã không tồn tại thì BE trả đúng `item_errors`
          // (`pos-variant.service.ts`), nên nó chưa từng chạy lần nào, kể cả
          // cho mã combo.
          const search = await posService.searchVariant(qrValue, fulfillmentMode);
          const needle = qrValue.trim().toLowerCase();

          // Khớp CHÍNH XÁC: đây là đường của súng quét, không phải đường gợi ý —
          // thêm nhầm combo vì trùng một phần chuỗi là hỏng đơn. Màn không hỗ
          // trợ combo (`onComboFound` vắng mặt) bỏ qua hoàn toàn nhánh combo.
          const exactCombo = onComboFound
            ? (search.combos ?? []).find(
                (combo) => combo.combo_sku.toLowerCase() === needle,
              )
            : undefined;
          if (exactCombo) {
            isAddingRef.current = false;
            setIsAdding(false);
            await selectCombo(exactCombo);
            return;
          }

          // Cùng kỷ luật "khớp chính xác" cho sản phẩm lẻ: gõ đúng mã nhưng
          // khác hoa/thường thì vẫn phải thêm được, còn gõ một phần tên thì
          // KHÔNG được tự chọn hộ.
          const exactVariant = [...(search.serials ?? []), ...(search.variants ?? [])].find(
            (item) => item.variant.variant_code.toLowerCase() === needle,
          );
          if (exactVariant) {
            const exactCode = exactVariant.serial
              ? `${exactVariant.variant.variant_code}_${exactVariant.serial}`
              : exactVariant.variant.variant_code;
            const retry = await posService.lookupVariants([exactCode], fulfillmentMode);
            detail = retry.items[0];
            if (!detail) {
              void message.error(retry.item_errors[0]?.error ?? 'Không tìm thấy sản phẩm');
              return;
            }
          } else {
            const suggestionCount =
              (search.serials?.length ?? 0) +
              (search.variants?.length ?? 0) +
              (search.combos?.length ?? 0);
            // Có gợi ý mà không khớp chính xác = thu ngân đang TÌM, chưa chọn.
            // Im lặng để dropdown gợi ý làm nốt việc; bắn toast đỏ ở đây chính
            // là lỗi "Không tìm thấy variant với mã: DR" của ticket.
            if (suggestionCount > 0) return;

            void message.error(lookup.item_errors[0]?.error ?? 'Không tìm thấy sản phẩm');
            return;
          }
        } else if (lookup.item_errors.length > 0) {
          void message.error(lookup.item_errors[0].error);
          return;
        }

        const variant = mapDetailToPosVariant(detail);
        onVariantFound(variant);
        onAdded?.();
      } catch {
        void message.error('Lỗi khi lấy thông tin sản phẩm');
      } finally {
        isAddingRef.current = false;
        setIsAdding(false);
      }
    },
    [fulfillmentMode, onVariantFound, onComboFound, onAdded, selectCombo],
  );

  const addFlashSaleItem = useCallback(
    async (variantCode: string, flashSaleItemId: string, flashPrice: number) => {
      if (isAddingRef.current) return;
      if (!onFlashSaleFound) return;
      isAddingRef.current = true;
      setIsAdding(true);
      try {
        const { items, item_errors } = await posService.lookupVariants(
          [variantCode],
          fulfillmentMode,
        );
        if (item_errors.length > 0) {
          void message.error(item_errors[0].error);
          return;
        }
        if (items.length === 0) {
          void message.warning('Không tìm thấy sản phẩm flash sale');
          return;
        }
        const variant = mapDetailToPosVariant(items[0]);
        onFlashSaleFound(variant, flashSaleItemId, flashPrice);
        onAdded?.();
      } catch {
        void message.error('Lỗi khi lấy thông tin sản phẩm flash sale');
      } finally {
        isAddingRef.current = false;
        setIsAdding(false);
      }
    },
    [fulfillmentMode, onFlashSaleFound, onAdded],
  );

  return { isAdding, addByQrCode, selectCombo, addFlashSaleItem };
}
